import React, { useCallback, useMemo, useState } from 'react'
import { StringParam, useQueryParams } from 'use-query-params'
import { useTranslation } from 'react-i18next'

import { dayJS } from '../../library/utility/date'
import { useFilterValue } from '../../hooks'

import { SearchField } from '@percent/admin-dashboard/common/components'
import styles from './SearchAndDateFilter.module.scss'
import { SearchAndDateFilterProps } from './SearchAndDateFilter.types'
import { Button, DateRange, Flex, Typography } from '@percent/lemonade'

export function SearchAndDateFilter({
  handleKeyPress,
  placeholder,
  searchPhrase,
  maxLength,
  setQueryParams,
  actions
}: SearchAndDateFilterProps) {
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useFilterValue(searchPhrase, [searchPhrase])
  const [{ startDate, endDate, ...restQueryParams }] = useQueryParams({
    startDate: StringParam,
    endDate: StringParam
  })

  const [isDatePickerOpened, setIsDatePickerOpened] = useState(false)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target

    if (maxLength && value.length > maxLength) {
      setSearchValue(value.slice(0, maxLength))
    } else {
      setSearchValue(value)
    }
  }

  const dateRangeQueryParams = useMemo(() => {
    if (startDate && endDate) {
      return {
        startDate,
        endDate
      }
    }

    if (isDatePickerOpened) {
      const defaultStart = dayJS().format('YYYY-MM-DDTHH:mm:ssZ')
      const defaultEnd = dayJS().format('YYYY-MM-DDTHH:mm:ssZ')

      return {
        startDate: defaultStart,
        endDate: defaultEnd
      }
    }

    return {
      startDate: undefined,
      endDate: undefined
    }
  }, [isDatePickerOpened, endDate, startDate])

  const handleDateRangeClick = useCallback(() => {
    setIsDatePickerOpened(open => !open)
  }, [])

  const handleClearDate = useCallback(
    () => setQueryParams?.({ startDate: undefined, endDate: undefined }),
    [setQueryParams]
  )

  return (
    <Flex gap={24} w="100%" align="center" justify="space-between">
      <div style={{ width: '70%' }}>
        <SearchField
          value={searchValue ?? ''}
          handleChange={handleChange}
          handleKeyPress={handleKeyPress}
          placeholder={placeholder}
        />
      </div>
      <Flex align="center" gap={16}>
        {actions}
        {setQueryParams && (
          <DateRange
            open={isDatePickerOpened}
            setOpen={setIsDatePickerOpened}
            queryParams={dateRangeQueryParams}
            setQueryParams={params => {
              if (typeof params === 'object') {
                setQueryParams?.({
                  ...restQueryParams,
                  startDate: params?.startDate,
                  endDate: params?.endDate
                })
                setIsDatePickerOpened(false)
              }
            }}
            applyButtonText={t('button.apply')}
            withinPortal={false}
            maxDate={dayJS().endOf('day').toDate()}
          >
            <Button
              size="small"
              variant="secondary"
              onClick={handleDateRangeClick}
              endIcon={isDatePickerOpened ? 'chevron-up' : 'chevron-down'}
              endIconSize={3}
            >
              {(startDate && endDate && `${dayJS(startDate).format('ll')} - ${dayJS(endDate).format('ll')}`) ||
                t('button.dateAll')}
            </Button>
          </DateRange>
        )}
        {setQueryParams && startDate && endDate && (
          <Typography variant="large" onClick={handleClearDate} className={styles.clearText}>
            {t('typography.clear')}
          </Typography>
        )}
      </Flex>
    </Flex>
  )
}
